<script setup lang="ts">
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import { computed, onMounted, ref, useSlots, watch } from 'vue'
import { countDecimals, debounce, uid } from '../utils'

type Type = 'text' | 'email' | 'date' | 'hidden' | 'number' | 'password' | 'search' | 'tel' | 'time' | 'url'
type Autocomplete =
    | 'on'
    | 'name'
    | 'given-name'
    | 'family-name'
    | 'nickname'
    | 'email'
    | 'username'
    | 'new-password'
    | 'current-password'
    | 'organization'
    | 'organization-title'
    | 'tel'
    | string

interface Props {
    label: string
    id?: string
    type?: Type
    name?: string
    value?: string | number
    placeholder?: string
    inputClass?: string
    inputStyle?: string
    helper?: string
    error?: string | boolean
    autocomplete?: Autocomplete
    required?: boolean
    disabled?: boolean
    labelHidden?: boolean
    focus?: boolean
    debounce?: number
    min?: number
    max?: number
    step?: string
    decimals?: number
}
const props = withDefaults(defineProps<Props>(), {
    id: undefined,
    type: 'text',
    name: undefined,
    value: undefined,
    placeholder: undefined,
    helper: undefined,
    error: undefined,
    autocomplete: undefined,
    required: false,
    disabled: false,
    labelHidden: false,
    focus: false,
    debounce: undefined,
    inputClass: undefined,
    inputStyle: undefined,
    min: undefined,
    max: undefined,
    step: undefined,
    decimals: undefined,
})
const input = ref<HTMLInputElement>()
const prefix = ref<HTMLDivElement>()
const suffix = ref<HTMLDivElement>()
const slots = useSlots()
const emit = defineEmits<{
    (event: 'update:value', value: string): void
}>()

watch(
    () => props.value,
    (value) => {
        if (props.type === 'number' && value) {
            const number = value as number

            if (props.max) {
                if (number > props.max) {
                    emit('update:value', props.max.toString())
                }
            }

            if (props.min !== undefined) {
                if (number < props.min) {
                    emit('update:value', props.min.toString())
                }
            }
        }

        if (props.type === 'number' && typeof props.decimals !== 'undefined' && value) {
            const number = typeof value === 'string' ? parseFloat(value) : value
            if (!isNaN(number) && countDecimals(number) > props.decimals) {
                emit('update:value', number.toFixed(props.decimals))
                return
            }
        }
    }
)

const onInput = debounce((e: InputEvent) => {
    emit('update:value', e.target.value)
}, props.debounce)
const hasIcon = computed(() => !!slots.icon)
const hasPrefix = computed(() => !!slots.prefix)
const hasSuffix = computed(() => !!slots.suffix)
const inputClassInternal = computed(() => [
    'input__element',
    props.inputClass,
    {
        'input__element--icon': hasIcon.value,
    },
])
const classes = computed(() => ({
    'input--error': props.error,
    'input--disabled': props.disabled,
}))

onMounted(() => {
    if (props.focus) input.value?.focus()
})

const isPasswordHidden = ref(true)
const onClickTogglePassword = () => {
    isPasswordHidden.value = !isPasswordHidden.value
}
const isPassword = computed(() => props.type === 'password')

const internalId = ref(props.id || uid())
const internalType = computed(() => (isPassword.value ? (isPasswordHidden.value ? 'password' : 'text') : props.type))

const internalInputStyle = computed(() => {
    let style = ''

    if (hasSuffix.value) {
        const suffixWidth = (suffix.value?.offsetWidth || 0) + 4
        style += `padding-right: ${suffixWidth}px; `
    }

    if (hasPrefix.value) {
        const prefixWidth = (prefix.value?.offsetWidth || 0) + 4
        style += `padding-left: ${prefixWidth}px; `
    }

    return style + props.inputStyle
})
</script>

<template>
    <div class="input" :class="classes">
        <label :id="`input-label-${internalId}`" :for="internalId" :class="['input__label', { 'sr-only': labelHidden }]">
            {{ label }}
        </label>
        <div class="relative">
            <input
                :id="internalId"
                ref="input"
                :name="name"
                :type="internalType"
                :required="required"
                :class="inputClassInternal"
                :value="value"
                :placeholder="placeholder"
                :disabled="disabled"
                :autocomplete="autocomplete"
                :style="internalInputStyle"
                :maxlength="max"
                :step="step"
                :aria-describedby="helper ? `input-helper-${internalId}` : undefined"
                :aria-labelledby="`input-label-${internalId}`"
                @input="onInput"
            />
            <div v-if="hasPrefix" ref="prefix" class="input__prefix">
                <slot name="prefix" />
            </div>
            <div v-if="hasSuffix" ref="suffix" class="input__suffix">
                <slot name="suffix" />
            </div>
            <slot name="icon" />
            <button
                v-if="isPassword"
                type="button"
                :class="['input__icon', 'input__icon--password']"
                tabindex="-1"
                @click="onClickTogglePassword"
            >
                <EyeIcon v-if="isPasswordHidden" class="w-full h-full" />
                <EyeSlashIcon v-else class="w-full h-full" />
            </button>
        </div>
        <p v-if="helper" :id="`input-helper-${internalId}`" class="input__helper">
            {{ helper }}
        </p>
        <p v-if="error" class="input__error">
            {{ error }}
        </p>
    </div>
</template>
